import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Lock,
  ArrowLeft,
  Eye,
  EyeOff,
} from 'lucide-react';

import PricingCalculatorSection from '@/features/pricing-calculator/components/PricingCalculatorSection';
import Button from '@/components/shared/Button';
import { useCreateCompany } from '@/hooks/useCompany';

/* ---------------------------------------------
  Fields
--------------------------------------------- */

const FIELDS = [
  {
    name: 'name_en',
    label: 'demo.fields.companyName',
    placeholder: 'demo.placeholders.companyName',
    icon: Building2,
    type: 'text',
  },
  {
    name: 'address_en',
    label: 'demo.fields.address',
    placeholder: 'demo.placeholders.address',
    icon: MapPin,
    type: 'text',
  },
  {
    name: 'phones',
    label: 'demo.fields.phone',
    placeholder: 'demo.placeholders.phone',
    icon: Phone,
    type: 'tel',
  },
  {
    name: 'email',
    label: 'demo.fields.email',
    placeholder: 'demo.placeholders.email',
    icon: Mail,
    type: 'email',
  },
  {
    name: 'password',
    label: 'demo.fields.password',
    placeholder: 'demo.placeholders.password',
    icon: Lock,
    type: 'password',
  },
];

/* ---------------------------------------------
  Highlights
--------------------------------------------- */

const HIGHLIGHTS = [
  'demo.highlights.setup',
  'demo.highlights.support',
];

/* ---------------------------------------------
  Book Demo Page
--------------------------------------------- */

export default function BookDemoPage() {
  const { t, i18n } = useTranslation();

  const currentLanguage =
    i18n.resolvedLanguage ||
    i18n.language ||
    'en';

  const isArabic = currentLanguage
    .toLowerCase()
    .startsWith('ar');

  const { submit, loading } = useCreateCompany();

  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    name_en: '',
    address_en: '',
    phones: '',
    email: '',
    password: '',
  });

  /* ---------------------------------------------
    Change
  --------------------------------------------- */

  const change = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ---------------------------------------------
    Submit
  --------------------------------------------- */

  const send = async (e) => {
    e.preventDefault();

    try {
      const result = await submit(form);

      toast.success(
        t(
          'demo.success',
          isArabic
            ? 'تم إنشاء حساب الشركة بنجاح'
            : 'Company account created successfully'
        )
      );

      const token = result?.token;

      if (token) {
        localStorage.setItem('token', token);
        window.location.href = `https://dashboard.nizamhr.com/sso?token=${encodeURIComponent(token)}`;
      } else {
        window.location.href = 'https://dashboard.nizamhr.com/';
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.errors?.email?.[0] ||
          err?.response?.data?.message ||
          t(
            'demo.error',
            isArabic
              ? 'حدث خطأ ما، حاول مرة أخرى'
              : 'Something went wrong. Please try again.'
          )
      );
    }
  };

  return (
    <>
      {/* ---------------------------------------------
        Book Demo
      --------------------------------------------- */}

      <section
        dir={isArabic ? 'rtl' : 'ltr'}
        className="
          flex
          min-h-screen
          items-center
          justify-center
          bg-[#F5F6F8]
          px-4
          py-12
          sm:py-20
      
        "
      >
        <div
          className="
            grid
            w-full
            max-w-4xl
            grid-cols-1
            overflow-hidden
            rounded-[28px]
            mt-12
            border
            border-[#162536]/5
            bg-white
            shadow-[0_30px_80px_-24px_rgba(22,37,54,0.35)]
            lg:grid-cols-[1fr_1.15fr]
       
          "
        >
          {/* ---------------------------------------------
            Brand Side
          --------------------------------------------- */}

          <div
            className="
              relative
              hidden
              flex-col
              justify-between
              overflow-hidden
              bg-[#162536]
              p-10
              text-white
              lg:flex
            "
          >
            {/* Decoration */}

            <div
              className={`
                pointer-events-none
                absolute
                -top-24
                h-64
                w-64
                rounded-full
                bg-[#C9A227]/10
                blur-3xl
                ${
                  isArabic
                    ? '-left-24'
                    : '-right-24'
                }
              `}
            />

            {/* Content */}

            <div className="relative">
              <span
                className="
                  text-xs
                  font-semibold
                  text-[#C9A227]
                "
              >
                {t('demo.brand')}
              </span>

              <h1
                className="
                  mt-6
                  text-[30px]
                  leading-[1.35]
                  font-extrabold
                "
              >
                {t('demo.titleLine1')}

                <br />

                {t('demo.titleLine2')}
              </h1>

              <p
                className="
                  mt-4
                  max-w-xs
                  text-[15px]
                  leading-7
                  text-white/70
                "
              >
                {t('demo.description')}
              </p>
            </div>

            {/* Highlights */}

            <ul
              className="
                relative
                space-y-3
                text-sm
                text-white/80
              "
            >
              {HIGHLIGHTS.map((item) => (
                <li
                  key={item}
                  className="
                    flex
                    items-center
                    gap-2.5
                  "
                >
                  <span
                    className="
                      h-1.5
                      w-1.5
                      shrink-0
                      rounded-full
                      bg-[#C9A227]
                    "
                  />

                  <span>
                    {t(item)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* ---------------------------------------------
            Form Side
          --------------------------------------------- */}

          <div className="p-8 sm:p-12">
            {/* Mobile Brand */}

            <span
              className="
                mb-6
                block
                text-xs
                font-semibold
                text-[#C9A227]
                lg:hidden
              "
            >
              {t('demo.brand')}
            </span>

            {/* Heading */}

            <h2
              className="
                text-2xl
                font-bold
                text-[#162536]
              "
            >
              {t('demo.formTitle')}
            </h2>

            <p
              className="
                mt-2
                text-sm
                text-slate-500
              "
            >
              {t('demo.formDescription')}
            </p>

            {/* ---------------------------------------------
              Form
            --------------------------------------------- */}

            <form
              onSubmit={send}
              className="mt-8 space-y-5"
            >
              {FIELDS.map(
                ({
                  name,
                  label,
                  placeholder,
                  icon: Icon,
                  type,
                }) => (
                  <div key={name}>
                    {/* Label */}

                    <label
                      htmlFor={name}
                      className="
                        mb-1.5
                        block
                        text-sm
                        font-medium
                        text-[#162536]
                      "
                    >
                      {t(label)}
                    </label>

                    {/* Input */}

                    <div className="relative">
                      <Icon
                        className={`
                          absolute
                          top-1/2
                          h-4
                          w-4
                          -translate-y-1/2
                          text-slate-400
                          ${
                            isArabic
                              ? 'right-3.5'
                              : 'left-3.5'
                          }
                        `}
                      />

                      <input
                        id={name}
                        required
                        type={
                          name === 'password'
                            ? showPassword
                              ? 'text'
                              : 'password'
                            : type
                        }
                        name={name}
                        value={form[name]}
                        onChange={change}
                        placeholder={t(placeholder)}
                        dir={isArabic ? 'rtl' : 'ltr'}
                        className={`
                          w-full
                          rounded-xl
                          border
                          border-slate-200
                          bg-slate-50/60
                          py-3
                          text-sm
                          text-[#162536]
                          outline-none
                          transition
                          focus:border-[#162536]
                          focus:bg-white
                          focus:ring-4
                          focus:ring-[#162536]/10
                          ${
                            name === 'password'
                              ? 'px-10'
                              : isArabic
                                ? 'pr-10 pl-4'
                                : 'pr-4 pl-10'
                          }
                        `}
                      />

                      {/* Password Visibility */}

                      {name === 'password' && (
                        <button
                          type="button"
                          aria-label={
                            showPassword
                              ? t(
                                  'demo.hidePassword',
                                  'Hide password'
                                )
                              : t(
                                  'demo.showPassword',
                                  'Show password'
                                )
                          }
                          onClick={() =>
                            setShowPassword(
                              (prev) => !prev
                            )
                          }
                          className={`
                            absolute
                            top-1/2
                            -translate-y-1/2
                            cursor-pointer
                            text-slate-400
                            transition
                            hover:text-[#162536]
                            ${
                              isArabic
                                ? 'left-3'
                                : 'right-3'
                            }
                          `}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )
              )}

              {/* ---------------------------------------------
                Submit
              --------------------------------------------- */}

              <Button
                type="submit"
                loading={loading}
                size="lg"
              >
                <span
                  className="
                    flex
                    items-center
                    justify-center
                    gap-2
                  "
                >
                  {t('demo.submit')}

                  <ArrowLeft
                    className={`
                      h-4
                      w-4
                      ${
                        isArabic
                          ? ''
                          : 'rotate-180'
                      }
                    `}
                  />
                </span>
              </Button>

              {/* ---------------------------------------------
                Terms
              --------------------------------------------- */}

              <p
                className="
                  text-center
                  text-xs
                  leading-5
                  text-slate-400
                "
              >
                {t(
                  'demo.consent',
                  isArabic
                    ? `بالضغط على ${t(
                        'demo.submit'
                      )} أنت توافق على شروط الاستخدام وسياسة الخصوصية`
                    : `By clicking ${t(
                        'demo.submit'
                      )}, you agree to the Terms of Use and Privacy Policy`
                )}
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------
        Pricing Calculator
      --------------------------------------------- */}

      <div
        dir={isArabic ? 'rtl' : 'ltr'}
        className="
          mx-auto
          mt-10
          w-full
          max-w-4xl
          px-4
        "
      >
        <PricingCalculatorSection />
      </div>
    </>
  );
}